import React, { useState, useEffect } from 'react';
import { schoolsAPI } from '../services/schoolsAPI';
import { useAuth } from '../contexts/AuthContext';
import { swrGet, swrSet } from '../utils/swrCache';
import { containedScroll, stickyPanelMaxHeight, useScrollportHeight } from '../utils/stickyScroll';
import {
  FilterSection,
  FilterCheckboxGroup,
  FilterRangeSection,
  FilterSearchableList,
  useFilterSections,
} from './filters';

export interface FilterState {
  division?: string[];
  conference?: string[];
  state?: string[];
  rosterStatus?: string[];
  majors?: string[];
  acceptanceRate?: { min?: number | null; max?: number | null };
  costOfAttendance?: { min?: number | null; max?: number | null };
  totalStudentCount?: { min?: number | null; max?: number | null };
}

interface FilterOptions {
  divisions?: string[];
  conferences?: string[];
  states?: string[];
  rosterStatuses?: string[];
  majors?: string[];
  acceptanceRateRange?: { min: number; max: number };
  costRange?: { min: number; max: number };
  studentCountRange?: { min: number; max: number };
}

interface FilterSidebarProps {
  onApplyFilters: (filters: FilterState) => void;
  onClearFilters: () => void;
  externalFilters?: FilterState; // Accept filters from parent to reset UI
  isMobileModal?: boolean; // When used in mobile modal, adjust styling
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ onApplyFilters, onClearFilters, externalFilters, isMobileModal = false }) => {
  const { sessionToken } = useAuth();
  // Initialize from the parent's active filters so a fresh mount (e.g. the mobile
  // filter modal, which REMOUNTS this component) already reflects them instead of
  // starting empty and wiping them on Apply (audit #171).
  const [filters, setFilters] = useState<FilterState>(() => externalFilters ?? {});
  const [options, setOptions] = useState<FilterOptions>({});
  const [loading, setLoading] = useState(true);
  // Sized to the app's scroll area below the header, so the panel fits on screen.
  const scrollportHeight = useScrollportHeight();
  const [sliderResetKey, setSliderResetKey] = useState(0); // Key to force slider re-render
  // Accordion: opening one section closes the rest (the college-search
  // behaviour). The shared hook owns it so the coach sidebar can opt out.
  const { expanded: expandedSections, toggle: toggleSection } = useFilterSections(
    {
      division: true,
      conference: false,
      state: false,
      rosterStatus: false,
      majors: false,
      cost: false,
      acceptance: false,
      studentCount: false,
    },
    { exclusive: true }
  );
  const longSectionOpen = !!(expandedSections.conference || expandedSections.state || expandedSections.majors);

  // Slider values
  const [costRange, setCostRange] = useState<[number, number]>([0, 100000]);
  const [studentCountRange, setStudentCountRange] = useState<[number, number]>([0, 50000]);
  const [acceptanceRange, setAcceptanceRange] = useState<[number, number]>([0, 100]);
  const [majorsSearch, setMajorsSearch] = useState('');

  useEffect(() => {
    loadFilterOptions();
  }, [sessionToken]);

  // Keep the internal select/array filters in sync with the parent's active
  // filters (audit #171). Keyed on externalFilters ONLY so it re-hydrates when the
  // parent's filters change (empty => reset, non-empty => restore) but does NOT
  // clobber in-progress selections when the options payload revalidates.
  useEffect(() => {
    if (!externalFilters) return;
    setFilters(externalFilters);
  }, [externalFilters]);

  // Derive the range sliders from the parent's filters, falling back to the full
  // option range when a bound isn't set. Depends on options for the defaults.
  useEffect(() => {
    if (!externalFilters) return;

    const costMin = options.costRange?.min ?? 0;
    const costMax = options.costRange?.max ?? 100000;
    setCostRange([
      externalFilters.costOfAttendance?.min ?? costMin,
      externalFilters.costOfAttendance?.max ?? costMax,
    ]);

    const studentMin = options.studentCountRange?.min ?? 0;
    const studentMax = options.studentCountRange?.max ?? 50000;
    setStudentCountRange([
      externalFilters.totalStudentCount?.min ?? studentMin,
      externalFilters.totalStudentCount?.max ?? studentMax,
    ]);

    const acceptMin = options.acceptanceRateRange?.min ?? 0;
    const acceptMax = options.acceptanceRateRange?.max ?? 100;
    setAcceptanceRange([
      externalFilters.acceptanceRate?.min ?? acceptMin,
      externalFilters.acceptanceRate?.max ?? acceptMax,
    ]);
  }, [externalFilters, options]);

  // Apply an options payload to state (shared by fetch + SWR cache paths).
  const applyOptions = (opts: any) => {
    setOptions(opts);

    // Set initial slider ranges
    if (opts.costRange) {
      setCostRange([opts.costRange.min, opts.costRange.max]);
    }
    if (opts.studentCountRange) {
      setStudentCountRange([opts.studentCountRange.min, opts.studentCountRange.max]);
    }
    if (opts.acceptanceRateRange) {
      setAcceptanceRange([opts.acceptanceRateRange.min, opts.acceptanceRateRange.max]);
    }
  };

  const loadFilterOptions = async () => {
    if (!sessionToken) return;

    // Stale-while-revalidate: options are global and change at most daily, so
    // paint the cached set instantly; the fetch still runs and replaces them.
    const cached = swrGet<any>('filterOptions', 30 * 60 * 1000);
    if (cached) applyOptions(cached);

    try {
      if (!cached) setLoading(true);
      const response = await schoolsAPI.getFilterOptions(sessionToken);
      const opts = response.filter_options;
      applyOptions(opts);
      swrSet('filterOptions', opts);
    } catch (error) {
      console.error('Error loading filter options:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if a section has active filters
  const hasSectionFilters = (section: string): boolean => {
    switch (section) {
      case 'division':
        return (filters.division && filters.division.length > 0) || false;
      case 'conference':
        return (filters.conference && filters.conference.length > 0) || false;
      case 'state':
        return (filters.state && filters.state.length > 0) || false;
      case 'rosterStatus':
        return (filters.rosterStatus && filters.rosterStatus.length > 0) || false;
      case 'majors':
        return (filters.majors && filters.majors.length > 0) || false;
      case 'cost':
        const costMin = options.costRange?.min || 0;
        const costMax = options.costRange?.max || 100000;
        return costRange[0] !== costMin || costRange[1] !== costMax;
      case 'acceptance':
        const acceptMin = options.acceptanceRateRange?.min || 0;
        const acceptMax = options.acceptanceRateRange?.max || 100;
        return acceptanceRange[0] !== acceptMin || acceptanceRange[1] !== acceptMax;
      case 'studentCount':
        const studentMin = options.studentCountRange?.min || 0;
        const studentMax = options.studentCountRange?.max || 50000;
        return studentCountRange[0] !== studentMin || studentCountRange[1] !== studentMax;
      default:
        return false;
    }
  };

  const handleMultiSelectToggle = (category: keyof FilterState, value: string) => {
    setFilters(prev => {
      const current = prev[category] as string[] || [];
      const updated = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      
      return {
        ...prev,
        [category]: updated.length > 0 ? updated : undefined
      };
    });
  };

  const handleApply = () => {
    const finalFilters: FilterState = { ...filters };
    
    // Only include ranges if they're not at extremes
    const costMin = options.costRange?.min || 0;
    const costMax = options.costRange?.max || 100000;
    if (costRange[0] !== costMin || costRange[1] !== costMax) {
      finalFilters.costOfAttendance = {
        min: costRange[0] === costMin ? null : costRange[0],
        max: costRange[1] === costMax ? null : costRange[1]
      };
    }
    
    const studentMin = options.studentCountRange?.min || 0;
    const studentMax = options.studentCountRange?.max || 50000;
    if (studentCountRange[0] !== studentMin || studentCountRange[1] !== studentMax) {
      finalFilters.totalStudentCount = {
        min: studentCountRange[0] === studentMin ? null : studentCountRange[0],
        max: studentCountRange[1] === studentMax ? null : studentCountRange[1]
      };
    }
    
    const acceptMin = options.acceptanceRateRange?.min || 0;
    const acceptMax = options.acceptanceRateRange?.max || 100;
    if (acceptanceRange[0] !== acceptMin || acceptanceRange[1] !== acceptMax) {
      finalFilters.acceptanceRate = {
        min: acceptanceRange[0] === acceptMin ? null : acceptanceRange[0],
        max: acceptanceRange[1] === acceptMax ? null : acceptanceRange[1]
      };
    }
    
    onApplyFilters(finalFilters);
  };

  const handleClear = () => {
    setFilters({});
    setMajorsSearch('');
    
    // Reset sliders to their default ranges
    const defaultCostMin = options.costRange?.min || 0;
    const defaultCostMax = options.costRange?.max || 100000;
    const defaultStudentMin = options.studentCountRange?.min || 0;
    const defaultStudentMax = options.studentCountRange?.max || 50000;
    const defaultAcceptMin = options.acceptanceRateRange?.min || 0;
    const defaultAcceptMax = options.acceptanceRateRange?.max || 100;
    
    setCostRange([defaultCostMin, defaultCostMax]);
    setStudentCountRange([defaultStudentMin, defaultStudentMax]);
    setAcceptanceRange([defaultAcceptMin, defaultAcceptMax]);
    
    // Force sliders to re-render
    setSliderResetKey(prev => prev + 1);
    
    onClearFilters();
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    }
    return `$${value}`;
  };

  // The in-section loading indicator, one copy instead of three. Kept as the
  // spinner this sidebar has always shown rather than promoted to Skeleton, so
  // the extraction moves no athlete-side pixels.
  const sectionLoading = (
    <div style={{ textAlign: 'center', padding: '16px', color: 'var(--aps-control-muted)' }} role="status" aria-label="Loading options">
      <div style={{
        display: 'inline-block',
        width: '16px',
        height: '16px',
        border: '2px solid var(--aps-control-border)',
        borderTop: '2px solid var(--aps-action)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
    </div>
  );

  return (
    <div style={{ width: isMobileModal ? '100%' : '280px', flexShrink: 0 }}>
      <div data-filter-sidebar style={{ 
        background: isMobileModal ? 'transparent' : 'white', 
        borderRadius: isMobileModal ? '0' : '12px', 
        border: isMobileModal ? 'none' : '1px solid #E5E7EB', 
        position: isMobileModal ? 'relative' : 'sticky', 
        top: isMobileModal ? 'auto' : '24px',
        maxHeight: isMobileModal ? 'none' : `${stickyPanelMaxHeight(scrollportHeight)}px`,
        display: 'flex',
        flexDirection: 'column' as const
      }}>
        {/* Fixed Header. In the drawer it sticks to the top while the list scrolls. */}
        <div style={isMobileModal
          ? { padding: '16px 20px 12px', flexShrink: 0, position: 'sticky', top: 0, zIndex: 1, background: 'white' }
          : { padding: '20px', flexShrink: 0, borderBottom: '1px solid #F3F4F6' }}>
          {!isMobileModal && (
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#151133', margin: '0 0 12px 0' }}>Filters</h3>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={handleApply}
              style={{ 
                flex: 1,
                padding: isMobileModal ? '14px' : '10px', 
                background: '#F9550D', 
                border: 'none', 
                borderRadius: '8px', 
                fontSize: isMobileModal ? '15px' : '13px', 
                fontWeight: '600', 
                color: 'white', 
                cursor: 'pointer', 
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#E04A0B'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#F9550D'}
            >
              Apply Filters
            </button>
            <button 
              onClick={handleClear}
              style={{ 
                flex: 1,
                padding: '10px',
                fontSize: '13px', 
                color: '#6B7280', 
                background: '#F9FAFB',
                border: '1px solid #E5E7EB', 
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#F3F4F6';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#F9FAFB';
                e.currentTarget.style.color = '#6B7280';
              }}
            >
              Clear
            </button>
          </div>
        </div>

        {/* On desktop the sections scroll when the panel is taller than the
            screen. With a long list open the scroll stays in the panel. With
            nothing long open, scrolling past the panel's end moves the page. */}
        <div
          data-filter-sections
          style={isMobileModal
            ? { padding: '0 20px' }
            : {
                flex: 1,
                minHeight: 0,
                ...containedScroll,
                overscrollBehavior: longSectionOpen ? 'contain' : 'auto',
                padding: '0 20px',
              }}
        >
        {/* Division */}
        <FilterSection
          id="filter-division"
          title="Division"
          expanded={expandedSections.division}
          onToggle={() => toggleSection('division')}
          active={hasSectionFilters('division')}
        >
          {loading ? sectionLoading : (
            <FilterCheckboxGroup
              ariaLabel="Division"
              options={options.divisions || []}
              selected={filters.division || []}
              onToggle={(value) => handleMultiSelectToggle('division', value)}
            />
          )}
        </FilterSection>

        {/* Conference */}
        <FilterSection
          id="filter-conference"
          title="Conference"
          expanded={expandedSections.conference}
          onToggle={() => toggleSection('conference')}
          active={hasSectionFilters('conference')}
        >
          {loading ? sectionLoading : (options.conferences || []).length > 0 ? (
            <FilterCheckboxGroup
              ariaLabel="Conference"
              options={options.conferences || []}
              selected={filters.conference || []}
              onToggle={(value) => handleMultiSelectToggle('conference', value)}
            />
          ) : (
            <div style={{ fontSize: '13px', color: 'var(--aps-control-muted)', padding: '8px 0' }}>No conferences available</div>
          )}
        </FilterSection>

        {/* State */}
        <FilterSection
          id="filter-state"
          title="State"
          expanded={expandedSections.state}
          onToggle={() => toggleSection('state')}
          active={hasSectionFilters('state')}
        >
          {options.states ? (
            <FilterCheckboxGroup
              ariaLabel="State"
              options={options.states}
              selected={filters.state || []}
              onToggle={(value) => handleMultiSelectToggle('state', value)}
            />
          ) : null}
        </FilterSection>

        {/* Roster Status */}
        <FilterSection
          id="filter-roster-status"
          title="Roster Status"
          expanded={expandedSections.rosterStatus}
          onToggle={() => toggleSection('rosterStatus')}
          active={hasSectionFilters('rosterStatus')}
        >
          <FilterCheckboxGroup
            ariaLabel="Roster Status"
            options={options.rosterStatuses || ['Open Roster', 'Limited Spots', 'No Openings']}
            selected={filters.rosterStatus || []}
            onToggle={(value) => handleMultiSelectToggle('rosterStatus', value)}
          />
        </FilterSection>

        {/* Majors Offered */}
        <FilterSection
          id="filter-majors"
          title="Majors Offered"
          expanded={expandedSections.majors}
          onToggle={() => toggleSection('majors')}
          active={hasSectionFilters('majors')}
        >
          <FilterSearchableList
            ariaLabel="Majors Offered"
            searchValue={majorsSearch}
            onSearchChange={setMajorsSearch}
            placeholder="Search majors..."
            options={options.majors || []}
            selected={filters.majors || []}
            onToggle={(value) => handleMultiSelectToggle('majors', value)}
            loading={loading}
            loadingNode={sectionLoading}
            emptyText="No majors available"
            noMatchText="No majors match your search"
          />
        </FilterSection>

        {/* Cost of Attendance Slider */}
        <FilterRangeSection
          id="cost"
          title="Cost of Attendance"
          expanded={expandedSections.cost}
          onToggle={() => toggleSection('cost')}
          active={hasSectionFilters('cost')}
          min={options.costRange?.min || 0}
          max={options.costRange?.max || 100000}
          value={costRange}
          onChange={setCostRange}
          formatLabel={(val) => formatCurrency(val)}
          step={1000}
          ariaLabel="Cost of attendance"
          resetKey={sliderResetKey}
        />

        {/* Acceptance Rate Slider */}
        <FilterRangeSection
          id="acceptance"
          title="Acceptance Rate"
          expanded={expandedSections.acceptance}
          onToggle={() => toggleSection('acceptance')}
          active={hasSectionFilters('acceptance')}
          min={options.acceptanceRateRange?.min || 0}
          max={options.acceptanceRateRange?.max || 100}
          value={acceptanceRange}
          onChange={setAcceptanceRange}
          formatLabel={(val) => `${val}%`}
          step={1}
          ariaLabel="Acceptance rate"
          resetKey={sliderResetKey}
        />

        {/* Student Count Slider — last section, no trailing rule */}
        <FilterRangeSection
          id="studentCount"
          title="Total Students"
          expanded={expandedSections.studentCount}
          onToggle={() => toggleSection('studentCount')}
          active={hasSectionFilters('studentCount')}
          divider={false}
          min={options.studentCountRange?.min || 0}
          max={options.studentCountRange?.max || 50000}
          value={studentCountRange}
          onChange={setStudentCountRange}
          formatLabel={(val) => val.toLocaleString()}
          step={1000}
          ariaLabel="Total students"
          resetKey={sliderResetKey}
        />
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;
